import type { SaunaSetupNoteView } from '@jums-dashboard/shared';
import { useState } from 'react';
import { LuPencil, LuTrash2 } from 'react-icons/lu';

import ConfirmModal from '@/components/ConfirmModal/ConfirmModal';
import IconButton from '@/components/IconButton/IconButton';
import {
  useCreateSaunaSetupNote,
  useDeleteSaunaSetupNote,
  useUpdateSaunaSetupNote,
} from '@/features/homeAutomation/hooks/useSaunaSetupNoteMutations';

import SaunaSetupNoteForm from './SaunaSetupNoteForm';
import { formatDateTime } from './sessionHistory';

export interface SaunaSetupNotesProps {
  homeId: string;
  notes: readonly SaunaSetupNoteView[];
}

/** Dated free-text notes about setup changes (sensor moved, new stove, …), shown as markers on the heating-rate chart. */
export default function SaunaSetupNotes({ homeId, notes }: SaunaSetupNotesProps) {
  const createNote = useCreateSaunaSetupNote();
  const updateNote = useUpdateSaunaSetupNote();
  const deleteNote = useDeleteSaunaSetupNote();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<SaunaSetupNoteView | null>(null);

  const confirmDelete = () => {
    if (!deleting) return;
    deleteNote.mutate({ homeId, noteId: deleting.noteId }, { onSettled: () => setDeleting(null) });
  };

  return (
    <div className="flex flex-col gap-3">
      <SaunaSetupNoteForm submitLabel="Add note" saving={createNote.isPending} onSubmit={(v) => createNote.mutateAsync({ homeId, ...v })} />

      {notes.length === 0 ? (
        <p className="text-sm text-(--text-tertiary)">No setup notes yet.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-(--border-subtle) rounded-md border border-(--border-subtle)">
          {notes.map((note) => (
            <li key={note.noteId} className="px-3 py-2 text-sm">
              {editingId === note.noteId ? (
                <SaunaSetupNoteForm
                  initialText={note.text}
                  initialNotedAt={note.notedAt}
                  submitLabel="Save"
                  saving={updateNote.isPending}
                  onSubmit={async (v) => {
                    await updateNote.mutateAsync({ homeId, noteId: note.noteId, ...v });
                    setEditingId(null);
                  }}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-(--text-tertiary)">{formatDateTime(note.notedAt)}</div>
                    <p className="break-words whitespace-pre-wrap text-(--text-primary)">{note.text}</p>
                  </div>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    icon={<LuPencil />}
                    aria-label="Edit note"
                    onClick={() => setEditingId(note.noteId)}
                  />
                  <IconButton size="sm" variant="ghost" icon={<LuTrash2 />} aria-label="Delete note" onClick={() => setDeleting(note)} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <ConfirmModal
        open={!!deleting}
        title="Delete setup note?"
        body={deleting ? `“${deleting.text}” will be removed from the history chart.` : undefined}
        confirmLabel="Delete"
        destructive
        loading={deleteNote.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
